import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Lightweight EN/ES language support (v1: nav, hero, footer).
 * Spanish is warm NYC Spanish, not textbook — more pages get translated
 * as copy is finalized (see master plan, prompt #11).
 */

export type Lang = "en" | "es";

const DICT: Record<string, { en: string; es: string }> = {
  // nav
  "nav.home": { en: "Home", es: "Inicio" },
  "nav.pickup": { en: "Schedule Pickup", es: "Agendar Recogida" },
  "nav.business": { en: "Business", es: "Negocios" },
  "nav.mission": { en: "Mission", es: "Misión" },
  "nav.learn": { en: "Learn", es: "Aprende" },
  "nav.impact": { en: "Impact", es: "Impacto" },
  "nav.rewards": { en: "Rewards", es: "Premios" },
  "nav.play": { en: "Play", es: "Jugar" },
  "nav.contact": { en: "Contact", es: "Contacto" },
  "nav.cta": { en: "Schedule Pickup", es: "Agendar Recogida" },
  "nav.waitlist": { en: "Join the Waitlist", es: "Únete a la Lista" },

  // hero
  "hero.badge": { en: "Made in New York City · All 5 boroughs", es: "Hecho en Nueva York · Los 5 condados" },
  "hero.sub": {
    en: "Everything Eco exists to prove that every person, every item, every community, and every opportunity has value. Free pickups turn what NYC throws away into second chances — for materials, for neighbors, for people.",
    es: "Everything Eco existe para demostrar que cada persona, cada cosa, cada comunidad y cada oportunidad tiene valor. Recogidas gratis que convierten lo que NYC desecha en segundas oportunidades — para materiales, para vecinos, para la gente.",
  },
  "hero.chip1": { en: "100% free, always", es: "100% gratis, siempre" },
  "hero.chip2": { en: "All 5 boroughs", es: "Los 5 condados" },
  "hero.chip3": { en: "Book in 2 minutes", es: "Agenda en 2 minutos" },
  "hero.btnPickup": { en: "📦 Schedule a Free Pickup", es: "📦 Agenda tu Recogida Gratis" },
  "hero.btnWaitlist": { en: "📬 Join the Pickup Waitlist", es: "📬 Únete a la Lista de Recogidas" },
  "hero.btnExperience": { en: "🎬 Watch the Experience", es: "🎬 Mira la Experiencia" },
  "hero.ecoforce": { en: "🦸 Meet the Eco-Force — play the game →", es: "🦸 Conoce a la Eco-Force — juega ya →" },

  // footer
  "footer.tagline": {
    en: "Making sustainable living easy and rewarding for all New Yorkers. One pickup at a time.",
    es: "Vivir sostenible, fácil y con recompensas para todos los neoyorquinos. Una recogida a la vez.",
  },
  "footer.services": { en: "Services", es: "Servicios" },
  "footer.electronics": { en: "Electronics Recycling", es: "Reciclaje de Electrónicos" },
  "footer.textile": { en: "Textile Recycling", es: "Reciclaje de Ropa" },
  "footer.junk": { en: "Junk Removal", es: "Retiro de Trastos" },
  "footer.schedule": { en: "Schedule a Pickup", es: "Agendar Recogida" },
  "footer.bizquotes": { en: "Business Quotes", es: "Cotización para Negocios" },
  "footer.company": { en: "Company", es: "Compañía" },
  "footer.mission": { en: "Our Mission", es: "Nuestra Misión" },
  "footer.vision": { en: "The Vision", es: "La Visión" },
  "footer.academy": { en: "Eco Academy", es: "Eco Academia" },
  "footer.privacy": { en: "Privacy Policy", es: "Política de Privacidad" },
  "footer.terms": { en: "Terms of Service", es: "Términos del Servicio" },
  "footer.impact": { en: "Our Impact", es: "Nuestro Impacto" },
  "footer.contact": { en: "Contact Us", es: "Contáctanos" },
  "footer.touch": { en: "Get in Touch", es: "Escríbenos" },
  "footer.rights": { en: "All rights reserved.", es: "Todos los derechos reservados." },
  "footer.slogan": {
    en: "We don’t waste. We work with what we’ve got. We give it a second life. ♻️",
    es: "No desperdiciamos. Trabajamos con lo que tenemos. Le damos una segunda vida. ♻️",
  },
};

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (k: string) => string };
const LangContext = createContext<Ctx>({ lang: "en", setLang: () => {}, t: (k) => k });

export const LangProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    try { return (localStorage.getItem("ee-lang") as Lang) || "en"; } catch { return "en"; }
  });
  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem("ee-lang", l); } catch {}
  };
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const t = (k: string) => (DICT[k] ? DICT[k][lang] : k);
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
